<script>
    import { onMount, onDestroy } from 'svelte';
    import { specStore, termsStore, cursorStore, editorStateStore } from '../../../stores/editorsStore.js';
    import { regenerativeContentStore } from '../../../stores/specContentStore';
    import { undoStore } from '../../../stores/undoStore.js';
    import { Editor } from '@tiptap/core';
    import StarterKit from '@tiptap/starter-kit';
    import Placeholder from '@tiptap/extension-placeholder';
    import Underline from '@tiptap/extension-underline';
    import Subscript from '@tiptap/extension-subscript';
    import Superscript from '@tiptap/extension-superscript';
    import { CustomHeading } from '../../../extensions/tiptap/customHeading';
    import { CustomParagraph } from '../../../extensions/tiptap/customParagraph';
    import { BlockInfo } from '../../../extensions/tiptap/blockInfo';
    import { BottomBlock } from '../../../extensions/tiptap/bottomBlock';
    import { FigureReference } from '../../../extensions/tiptap/figureReference';
    import { TermHighlighter } from '../../../extensions/tiptap/termHighlighter';
    import { SearchHighlighterTipTap } from '../../../extensions/tiptap/searchHighlighter';
    import { SlashCommand } from '../../../extensions/tiptap/slashCommand';
    import { AtCommand } from '../../../extensions/tiptap/atCommand';
    import { MenuHandler } from '../../../extensions/tiptap/menuHandler';
    import { AbstractWordLimit } from '../../../extensions/tiptap/abstractWordLimit';
    import { DuplicateSectionChecker } from '../../../extensions/tiptap/duplicateSectionChecker';
    import { InlineComment, stripInlineComments } from '../../../extensions/tiptap/inlineCommentMark';
    import { CommentAnchor } from '../../../extensions/tiptap/commentAnchor';
    import { Spellcheck } from '../../../extensions/tiptap/spellcheck';
    import { boilerplateService } from '../../../services/boilerplateService';
    import { figureService } from '../../../services/figureService';
    import { EditorContentService } from '../../../services/specEditorContentService';
    import { SpecTermUpdateService } from '../../../services/specTermUpdateService';
    import { spellcheckService } from '../../../services/spellcheckService';
    import { documentStructureService } from '../../../services/documentStructureService';
    import { navigationStore } from '../../../stores/navigationStore';
    import { commentsStore } from '../../../stores/commentsStore';
    import { documentMetadataStore } from '../../../stores/documentMetadataStore';
    import { termAutoScrollService } from '../../../services/termAutoScrollService.js';
    import { outlineGenerationService } from '../../../services/outlineGenerationService.js';
    import MenuManager from './menus/MenuManager.svelte';
    import NavigationPane from '../../spec/navigation/NavigationPane.svelte';
    import FigurePreview from '../../figures/FigurePreview.svelte';
    import NotificationBubble from '../../shared/NotificationBubble.svelte';
    import SpecProgressBar from './SpecProgressBar.svelte';
    import FigureReferenceStyles from '../../../styles/figure-reference-styles.svelte';
    import EditorBaseStyles from '../../../styles/spec/editor-base-styles.svelte';
    import EditorSectionStyles from '../../../styles/spec/editor-section-styles.svelte';
    import EditorBlockStyles from '../../../styles/spec/editor-block-styles.svelte';
    import EditorScrollbarStyles from '../../../styles/spec/editor-scrollbar-styles.svelte';
    import InlineCommentStyles from '../../../styles/spec/inline-comment-styles.svelte';
    import CommentAnchorStyles from '../../../styles/comments/comment-anchor-styles.svelte';
    import SpellcheckStyles from '../../../styles/spellcheck-styles.svelte';
    import { createFigureClickHandler } from '../../../utils/figureHandlers';
    import { blocks, sectionHeaders, contextualBlocks, attributeDisplayNames } from './blocks/blockDefinitions.js';

    // Helper functions from blockInfo.js
    function findBlockDefinition(node) {
        // Check if it's a title
        if (node.attrs.level === 1 && node.type.name === 'heading') {
            const titleBlock = blocks.find(b => b.id === 'title');
            return titleBlock;
        }
        
        // Check if it's a section header
        if (node.attrs.sectionType && node.type.name === 'heading') {
            const sectionBlock = sectionHeaders.find(h => h.id === node.attrs.sectionType);
            return sectionBlock;
        }
        
        // Check if it's a contextual block
        if (node.attrs.context && node.attrs.blockType) {
            const contextBlocks = contextualBlocks[node.attrs.context] || [];
            const contextBlock = contextBlocks.find(b => b.id === node.attrs.blockType);
            return contextBlock;
        }
        
        // Check regular blocks
        const regularBlock = blocks.find(b => b.id === node.attrs.blockType);
        
        // If no block definition found but it's a heading, return a default heading definition
        if (!regularBlock && node.type.name === 'heading') {
            return {
                displayAttributes: ['nodeType', 'level']
            };
        }
        
        return regularBlock;
    }

    function getDisplayValue(key, value) {
        // Convert value to string if it's not already
        const stringValue = String(value);
        
        // Special handling for nodeType values
        if (key === 'nodeType') {
            return stringValue === 'heading' ? 'Heading' : 'Paragraph';
        }
        
        // For other values, just capitalize first letter and return
        return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
    }

    let element;
    let editor;
    export let editorView;
    let activePreview = null;
    let specTermService;
    let menuManager;
    let wordCount = 0;
    
    // Expose undo/redo functions for external use
    export function performUndo() {
        if (editor && editor.can().undo()) {
            editor.chain().focus().undo().run();
        }
    }
    
    export function performRedo() {
        if (editor && editor.can().redo()) {
            editor.chain().focus().redo().run();
        }
    }
    
    // Update undo/redo state in store
    function updateUndoState() {
        if (!editor) return;
        
        // Safe way to check undo/redo availability
        const canUndo = editor.can().undo();
        const canRedo = editor.can().redo();
        
        undoStore.updateSpecUndoState(canUndo, canRedo);
    }
    
    // Progress bar state - exposed for parent to control
    export let progressVisible = false;
    export let progressStepsRemaining = 0;
    export let progressTotalSteps = 0;
    export let progressEstimatedTime = null;
    export let progressPercent = 0;
    
    // Abstract word count warning
    $: abstractExceedsLimit = $editorStateStore.abstractExceedsLimit;
    $: abstractWordCount = $editorStateStore.abstractWordCount;
    
    // Duplicate sections warning
    $: duplicateSections = $editorStateStore.duplicateSections || [];

    // Initialize services
    const contentService = new EditorContentService(specStore, regenerativeContentStore);

    // Function to calculate word count from editor document
    function calculateWordCount(doc) {
        if (!doc) return 0;
        
        let text = '';
        // Walk through all nodes and collect text with spaces between nodes
        doc.descendants((node) => {
            if (node.isText) {
                text += node.text;
            } else if (node.isBlock && text && !text.endsWith(' ')) {
                // Add space after block nodes to separate words
                text += ' ';
            }
        });
        
        // Strip inline comments before counting
        const textWithoutComments = stripInlineComments(text);
        // Match words - must contain at least one letter or number
        // This prevents standalone punctuation from being counted as words
        const words = textWithoutComments.trim().match(/\b[\w'-]+\b/g);
        return words ? words.length : 0;
    }

    // Function to extract title from the document
    function extractDocumentTitle(doc) {
        if (!doc) return null;
        
        let titleText = null;
        
        // Walk through nodes to find the title block
        doc.descendants((node, pos) => {
            // Look for h1 heading with blockType='title'
            if (node.type.name === 'heading' && 
                node.attrs.level === 1 && 
                node.attrs.blockType === 'title') {
                
                // Extract text content from the title node
                titleText = node.textContent;
                return false; // Stop searching once we find the title
            }
        });
        
        return titleText;
    }

    // Initialize term service
    $: {
        if (!specTermService) {
            specTermService = new SpecTermUpdateService(termsStore);
        }
    }

    // Create initial outline for empty documents
    function createInitialOutlineIfEmpty(editor) {
        // Check if the document is empty (only has default empty content)
        const content = editor.getHTML();
        const textContent = editor.state.doc.textContent.trim();
        console.log('SpecEditor: checking if empty');
        console.log('  - HTML content:', content);
        console.log('  - Text content:', textContent);
        console.log('  - Has section-type:', content.includes('data-section-type'));

        // Document is empty if it has no text and no section headers
        const isEmpty = !textContent && !content.includes('data-section-type');

        if (!isEmpty) {
            console.log('SpecEditor: has content, skipping initial outline');
            return;
        }

        console.log('SpecEditor: creating initial outline');

        // Get default sections from sectionHeaders (single source of truth)
        const defaultSections = sectionHeaders
            .filter(s => s.defaultEnabled)
            .map(s => s.id);

        try {
            const result = outlineGenerationService.createOutline(editor, defaultSections);

            if (result.success) {
                console.log('SpecEditor: initial outline created:', result);
            } else {
                console.error('SpecEditor: failed to create outline:', result.errors);
            }
        } catch (error) {
            console.error('SpecEditor: exception creating outline:', error);
        }
    }

    // Initialize term highlighter
    const termHighlighterExtension = TermHighlighter.configure({
        termsStore
    });
    
    // Initialize search highlighter
    const searchHighlighterExtension = SearchHighlighterTipTap.configure({
        highlightClass: 'search-result-highlight',
        currentResultClass: 'current'
    });
    
    // Initialize slash command extension
    const slashCommandExtension = SlashCommand.configure({
        onTrigger: (pos) => {
            if (menuManager && menuManager.openBlockMenu) {
                menuManager.openBlockMenu(pos);
            }
        }
    });
    
    // Initialize at command extension
    const atCommandExtension = AtCommand.configure({
        onTrigger: (pos) => {
            if (menuManager && menuManager.openFigureMenu) {
                menuManager.openFigureMenu(pos);
            }
        }
    });
    
    // Initialize menu handler extension
    const menuHandlerExtension = MenuHandler.configure({
        onSelectionChange: (selection) => {
            if (menuManager && menuManager.closeMenus) {
                menuManager.closeMenus();
            }
        }
    });

    // Initialize Tiptap editor

    onMount(async () => {
        // Initialize spellcheck service (non-blocking - loads dictionaries in background)
        spellcheckService.initialize().catch(err => {
            console.error('Failed to initialize spellcheck:', err);
        });

        editor = new Editor({
            element: element,
            extensions: [
                StarterKit.configure({
                    document: {
                        content: 'heading block*',
                    },
                    heading: false,  // We use CustomHeading instead
                    paragraph: false,  // We use CustomParagraph instead
                    orderedList: false,  // Disable automatic list creation when typing "1."
                    bulletList: false,  // Also disable bullet lists for consistency
                }),
                Underline,
                Subscript.extend({
                    addKeyboardShortcuts() {
                        return {
                            'Mod-ArrowDown': () => this.editor.commands.toggleSubscript(),
                        }
                    },
                }),
                Superscript.extend({
                    addKeyboardShortcuts() {
                        return {
                            'Mod-ArrowUp': () => this.editor.commands.toggleSuperscript(),
                        }
                    },
                }),
                CustomHeading,
                CustomParagraph,
                BlockInfo,
                BottomBlock,
                FigureReference,
                AbstractWordLimit.configure({
                    onUpdate: ({ wordCount, exceedsLimit }) => {
                        editorStateStore.update(state => ({
                            ...state,
                            abstractWordCount: wordCount,
                            abstractExceedsLimit: exceedsLimit
                        }));
                    }
                }),
                InlineComment,
                DuplicateSectionChecker.configure({
                    onUpdate: (duplicates) => {
                        editorStateStore.update(state => ({
                            ...state,
                            duplicateSections: duplicates
                        }));
                    }
                }),
                CommentAnchor.configure({
                    onCommentClick: (commentId, anchorId) => {
                        // Open the comments panel if it's not already open
                        commentsStore.open();
                        // Select the clicked comment
                        commentsStore.selectComment(commentId);
                    }
                }),
                termHighlighterExtension,
                searchHighlighterExtension,
                slashCommandExtension,
                atCommandExtension,
                menuHandlerExtension,
                Spellcheck,
                Placeholder.configure({
                    placeholder: ({ node }) => {
                        if (node.type.name === 'heading') {
                            return '';
                        }
                        return 'Write something, or press "/" for commands...';
                    },
                }),
            ],
            content: $specStore.content || '',
            autofocus: true,
            editable: true,
            onTransaction: ({ editor, transaction }) => {
                if (transaction.docChanged) {
                    contentService.updateContent(editor.getHTML());
                    wordCount = calculateWordCount(editor.state.doc);
                    // Update document structure for navigation
                    documentStructureService.updateStructure(editor.state.doc);
                    // Extract and update document title
                    const title = extractDocumentTitle(editor.state.doc);
                    documentMetadataStore.setTitle(title);
                    // Update undo/redo state
                    updateUndoState();
                }
            },
            onUpdate: ({ editor }) => {
                contentService.updateContent(editor.getHTML());
                wordCount = calculateWordCount(editor.state.doc);
                // Update undo/redo state
                updateUndoState();
            },
            onSelectionUpdate: ({ editor }) => {
                // Track current heading based on cursor position
                const cursorPos = editor.state.selection.from;
                const currentHeadingId = documentStructureService.findCurrentHeading(editor.state.doc, cursorPos);
                navigationStore.updateCurrentHeading(currentHeadingId);
            },
            onCreate: ({ editor }) => {
                editorView = editor;
                // Store editor in specStore for other components to access
                specStore.update(store => ({ ...store, editor }));
                // Expose to window for debugging
                if (typeof window !== 'undefined') {
                    window.specEditor = editor;
                }
                // Initialize word count
                wordCount = calculateWordCount(editor.state.doc);
                // Initialize document structure
                documentStructureService.updateStructure(editor.state.doc);
                // Extract and set initial document title
                const title = extractDocumentTitle(editor.state.doc);
                documentMetadataStore.setTitle(title);
                // Register the editor with the auto-scroll service
                termAutoScrollService.setSpecEditor(editor);
                // Expose to window for comment form access
                window.specEditor = editor;
                // Create initial outline if document is empty
                createInitialOutlineIfEmpty(editor);
            },
            onFocus: ({ editor }) => {
                // Update cursor store to indicate spec editor is active
                cursorStore.update(store => ({ ...store, activeEditor: 'spec' }));
            },
            onDestroy: () => {
                editorView = null;
                // Clear editor from specStore
                specStore.update(store => ({ ...store, editor: null }));
                // Clean up window reference
                if (window.specEditor === editor) {
                    window.specEditor = null;
                }
            },
            editorProps: {
                attributes: {
                    spellcheck: 'false'
                },
                handleClick: (view, pos, event) => {
                    // Handle info button clicks
                    if (event.target.closest('.block-info-button') || event.target.classList.contains('block-info-button')) {
                        // Find the button
                        const infoButton = event.target.closest('.block-info-button') || event.target;
                        
                        try {
                            // Remove any existing popups first
                            const existingPopups = document.querySelectorAll('.block-info-popup');
                            existingPopups.forEach(popup => {
                                popup.remove();
                                // Also clean up any associated event listeners
                                document.removeEventListener('click', popup._closeHandler);
                            });
                            
                            // Get the node type and attributes from the data attributes
                            const nodeType = infoButton.getAttribute('data-node-type');
                            const attrsStr = infoButton.getAttribute('data-node-attrs');
                            
                            if (nodeType && attrsStr) {
                                // Parse the attributes
                                const attrs = JSON.parse(attrsStr.replace(/\\'/g, "'"));
                                
                                // Find block definition to get displayAttributes
                                const node = { type: { name: nodeType }, attrs };
                                const blockDef = findBlockDefinition(node);
                                const displayAttributes = blockDef?.displayAttributes || ['nodeType'];
                                
                                // Get the block properties
                                const props = { nodeType, ...attrs };
                                
                                // Create the popup
                                const popup = document.createElement('div');
                                popup.className = 'block-info-popup';
                                popup.style.position = 'absolute';
                                popup.style.left = `${event.pageX + 10}px`;
                                popup.style.top = `${event.pageY + 10}px`;
                                popup.style.zIndex = '1000';
                                popup.style.background = 'var(--color-spec-info-box-bg)';
                                popup.style.padding = 'var(--spacing-2)';
                                popup.style.borderRadius = 'var(--border-radius-md)';
                                popup.style.boxShadow = 'var(--shadow-md)';
                                popup.style.fontSize = 'var(--font-size-xs)';
                                popup.style.color = 'var(--color-text-primary)';
                                
                                // Format and display only the specified properties with friendly names
                                const content = displayAttributes
                                    .filter(key => props[key] !== null && props[key] !== undefined)
                                    .map(key => {
                                        const displayName = attributeDisplayNames[key] || key;
                                        const displayValue = getDisplayValue(key, props[key]);
                                        return `<div><strong>${displayName}:</strong> ${displayValue}</div>`;
                                    })
                                    .join('');
                                
                                popup.innerHTML = content;
                                
                                // Add to document
                                document.body.appendChild(popup);
                                
                                // Add a handler to close when clicking elsewhere and store it on the popup
                                setTimeout(() => {
                                    function closePopup(e) {
                                        if (!popup.contains(e.target)) {
                                            popup.remove();
                                            document.removeEventListener('click', closePopup);
                                        }
                                    }
                                    
                                    // Store the handler on the popup for later cleanup
                                    popup._closeHandler = closePopup;
                                    document.addEventListener('click', closePopup);
                                }, 100);
                            }
                        } catch (error) {
                            console.error('Error showing block info:', error);
                        }
                        
                        return true; // Prevent further handling
                    }
                    
                    // Handle figure clicks
                    const figureRef = event.target.closest('.figure-reference');
                    if (figureRef) {
                        const figureKey = figureRef.getAttribute('data-figure-key');
                        figureService.getFigureByKey(figureKey).then(figure => {
                            if (figure) {
                                const rect = figureRef.getBoundingClientRect();
                                activePreview = { figure, referenceRect: rect };
                            }
                        }).catch(error => {
                            console.error(`[FigureHandler] Error fetching figure ${figureKey}:`, error);
                        });
                        return true;
                    }

                    // For all other clicks, explicitly set the selection to the clicked position.
                    // This is needed because when the editor regains focus, ProseMirror restores
                    // the previous selection before the click handler runs.
                    const tr = view.state.tr.setSelection(
                        view.state.selection.constructor.near(view.state.doc.resolve(pos))
                    );
                    view.dispatch(tr);
                    return true;
                }
            }
        });
        specTermService.registerEditor('spec', editor);
        editorView = editor.view;
        
        // Add aria-label to the ProseMirror element for accessibility
        setTimeout(() => {
            const proseMirrorEl = element.querySelector('.ProseMirror');
            if (proseMirrorEl) {
                proseMirrorEl.setAttribute('aria-label', 'Specification editor content');
            }
        }, 0);
    });

    // Subscribe to terms store changes
    $: if (editor && $termsStore.selectedEntries) {
        editor.view.dispatch(editor.view.state.tr);
    }


    // Subscribe to the spec store to keep editor content in sync
    $: if (editor && $specStore.content !== editor.getHTML()) {
        contentService.updateFromStore(editor);
    }
    
    // Extend editor to add search commands
    $: if (editor) {
        // Add getText method directly to editor object
        editor.getText = function() {
            return editor ? editor.state.doc.textContent : '';
        };
        
        // Override focus to handle both Tiptap focus and selection
        const originalFocus = editor.commands.focus;
        editor.commands.focus = (options) => {
            originalFocus(options);
            return true;
        };
        
        // Add text selection command to handle search results
        editor.commands.setTextSelection = ({ from, to }) => {
            editor.chain().setTextSelection({ from, to }).focus().run();
            return true;
        };
        
        // Add search results command
        editor.commands.setSearchResults = (results, currentIndex) => {
            editor.commands.setSearchHighlighter({
                searchResults: results,
                currentResultIndex: currentIndex
            });
            return true;
        };
    }

    // Subscribe to regenerative content
    $: {
        if (editor && $regenerativeContentStore !== editor.getHTML()) {
            contentService.updateFromRegenerativeStore(editor);
        }
    }

    onDestroy(() => {
        if (editor) {
            editor.destroy();
        }
        contentService.destroy();
        if (specTermService) {
            specTermService.destroy();
        }
        documentStructureService.destroy();
        navigationStore.reset();
        // Clean up window reference
        if (window.specEditor) {
            window.specEditor = null;
        }
    });
</script>

<FigureReferenceStyles />
<EditorBaseStyles />
<EditorSectionStyles />
<EditorBlockStyles />
<EditorScrollbarStyles />
<InlineCommentStyles />
<CommentAnchorStyles />
<SpellcheckStyles />

<div class="spec-editor" data-testid="spec-editor">
    <div class="spec-header">
        <h2>Spec</h2>
        {#if abstractExceedsLimit}
            <div class="abstract-warning-container">
                <NotificationBubble 
                    show={true}
                    message={`Abstract exceeds 150 words (currently ${abstractWordCount})`}
                    variant="error"
                    position="top"
                    duration={0}
                />
            </div>
        {/if}
        {#if duplicateSections.length > 0}
            <div class="duplicate-sections-warning-container">
                {#each duplicateSections as duplicate}
                    <NotificationBubble 
                        show={true}
                        message={`You can have only one ${duplicate.displayName} section`}
                        variant="error"
                        position="top"
                        duration={0}
                    />
                {/each}
            </div>
        {/if}
    </div>
    <div class="editor-container">
        <div
            class="tiptap-editor"
            bind:this={element}
        ></div>
        
        <SpecProgressBar
            isVisible={progressVisible}
            stepsRemaining={progressStepsRemaining}
            totalSteps={progressTotalSteps}
            estimatedTime={progressEstimatedTime}
            progress={progressPercent}
            on:stop
        />
        
        <MenuManager {editor} bind:this={menuManager} />

        {#if activePreview}
            <FigurePreview 
                figure={activePreview.figure}
                referenceRect={activePreview.referenceRect}
                on:close={() => activePreview = null}
            />
        {/if}
        
    </div>
    
    <!-- Navigation Pane -->
    <NavigationPane {editor} />
    <div class="word-count-indicator" aria-label="Word count">
        {wordCount} {wordCount === 1 ? 'word' : 'words'}
    </div>
</div>

<style>
    .spec-editor {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: visible;
        background: transparent;
    }

    .spec-header {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: var(--spacing-2) var(--spacing-4);
        overflow: visible;
    }

    h2 {
        margin: 0;
        font-size: var(--font-size-xl);
        font-weight: var(--font-weight-semibold);
        color: var(--color-spec-figure-text);
        background: transparent;
    }
    
    .abstract-warning-container {
        position: absolute;
        top: var(--spacing-6);
        left: 50%;
        z-index: var(--z-index-notification);
        transform: translateX(-50%);
        pointer-events: none;
    }
    
    .duplicate-sections-warning-container {
        position: absolute;
        top: var(--spacing-6);
        left: 50%;
        z-index: var(--z-index-notification);
        display: flex;
        flex-direction: column;
        align-items: center;
        transform: translateX(-50%);
        pointer-events: none;
        gap: var(--spacing-2);
    }
    
    /* Stack warnings vertically when both are present */
    .abstract-warning-container ~ .duplicate-sections-warning-container {
        top: calc(var(--spacing-6) + 48px); /* Adjust based on notification height */
    }

    .editor-container {
        position: relative;
        display: flex;
        flex: 1;
        flex-direction: column;
        min-height: 0;
        overflow: auto;
        background: transparent;
        border: none;
    }

    .tiptap-editor {
        position: relative;
        flex: 1;
        min-height: 0;
    }

    .word-count-indicator {
        position: absolute;
        right: var(--spacing-2);
        bottom: calc(var(--spacing-4) * -1);
        z-index: 1;
        padding: var(--spacing-1) var(--spacing-2);
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
        pointer-events: none;
        transition: var(--transition-opacity);
    }
</style>
